// deployment_class: customer_env_allowed
/**
 * The Analyzer — rules-based project risk/perf assessment (V4_1b Theme 8 v1).
 * bug_UI_1k: compare projects + identify risk. v1 is TRANSPARENT and GROUNDED —
 * every risk contribution cites its figure (DSCR vs the 1.20 floor, RAG status,
 * lifecycle progress). No backend / no model — the probabilistic (Kathedral-IQ)
 * version is v2 per the spec. "Verify me, not trust me."
 */
import { type Deal } from './project-data';

/** Your debt-service coverage floor (CLAUDE.md business rule). */
export const DSCR_FLOOR = 1.2;

export interface RiskSignal { label: string; weight: number; }
export interface RiskAssessment {
  score: number;                 // 0–100 (higher = more risk)
  level: 'Low' | 'Medium' | 'High';
  tone: 'g' | 'y' | 'r';
  signals: RiskSignal[];         // the cited contributions
}

/** Parse a DSCR string like "1.18 ⚠" or "1.31" → 1.18 / 1.31 (NaN-safe). */
export function parseDscr(dscr: string): number {
  return parseFloat(dscr);
}

/** Transparent, rules-based risk assessment for one deal. */
export function assessRisk(d: Deal): RiskAssessment {
  const signals: RiskSignal[] = [];
  const dscr = parseDscr(d.dscr);
  if (!Number.isNaN(dscr)) {
    if (dscr < DSCR_FLOOR) signals.push({ label: `DSCR ${dscr.toFixed(2)} is below your ${DSCR_FLOOR.toFixed(2)} floor`, weight: 40 });
    else if (dscr < 1.25) signals.push({ label: `DSCR ${dscr.toFixed(2)} is thin — close to the ${DSCR_FLOOR.toFixed(2)} floor`, weight: 15 });
  }
  if (d.rag[0] === 'r') signals.push({ label: `Flagged at risk — ${d.rag[1]}`, weight: 35 });
  else if (d.rag[0] === 'y') signals.push({ label: `On watch — ${d.rag[1]}`, weight: 18 });
  if (d.progress < 40) signals.push({ label: `Early in the lifecycle (${d.progress}% complete)`, weight: 12 });

  const score = Math.min(100, signals.reduce((s, x) => s + x.weight, 0));
  const level: RiskAssessment['level'] = score >= 50 ? 'High' : score >= 25 ? 'Medium' : 'Low';
  const tone: RiskAssessment['tone'] = level === 'High' ? 'r' : level === 'Medium' ? 'y' : 'g';
  return { score, level, tone, signals };
}

/**
 * Kathedral's read across a set of projects (v1 · deterministic, grounded).
 * Ranks by risk, names the highest + lowest, and lists what to watch. Returns
 * trusted static markup (bolded names). v2 swaps this for the AI-Gateway pass.
 */
export function generateRead(keys: string[], deals: Record<string, Deal>): string {
  // Takes the deals rather than importing a constant: this module used to read
  // `DEALS` from `project-data.ts`, which is how demo projects reached a pure
  // function. It now assesses whatever it is handed.
  const ranked = keys
    .map((k) => ({ d: deals[k], r: assessRisk(deals[k]) }))
    .filter((x) => x.d !== undefined)
    .sort((a, b) => b.r.score - a.r.score);
  if (ranked.length < 2) return '';

  const top = ranked[0];
  const low = ranked[ranked.length - 1];
  const parts: string[] = [];
  parts.push(
    `<b>${top.d.name}</b> carries the most risk (${top.r.score}/100 · ${top.r.level})` +
      (top.r.signals[0] ? ` — ${top.r.signals[0].label.toLowerCase()}.` : '.'),
  );
  if (low.r.score < top.r.score) {
    parts.push(`<b>${low.d.name}</b> is the strongest of the set (${low.r.score}/100 · ${low.r.level}).`);
  }
  const watch = ranked.filter((x) => x.r.level !== 'Low');
  if (watch.length) {
    parts.push(`Watch: ${watch.map((x) => `<b>${x.d.name}</b> (${x.d.next[0] ?? 'next steps pending'})`).join('; ')}.`);
  }
  parts.push('Every figure traces to the signed JV agreement — verify me, not trust me.');
  return parts.join(' ');
}
